import { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, FileText, 
  Settings, LogOut, Menu, X, ChevronDown, Award, PenSquare, FolderTree, Image, Truck 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSEO } from '@/utils/seo';

const navItems = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'Products', href: '/admin/products', icon: Package },
  { name: 'Categories', href: '/admin/categories', icon: FolderTree },
  { name: 'Orders', href: '/admin/orders', icon: ShoppingCart },
  { name: 'Customers', href: '/admin/customers', icon: Users },
  { name: 'Journal', href: '/admin/journal', icon: FileText },
  { name: 'Content (CMS)', href: '/admin/cms', icon: PenSquare },
  { name: 'Image Library', href: '/admin/images', icon: Image },
  { name: 'Shipping', href: '/admin/shipping', icon: Truck },
  { name: 'Loyalty', href: '/admin/loyalty', icon: Award },
];

const AdminLayout = () => {
  useSEO(); // sets noindex,nofollow on all /admin routes
  const navigate = useNavigate();
  const location = useLocation();
  const [adminToken, setAdminToken] = useState(localStorage.getItem('chaleur_admin_token'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin/login');
    }
  }, [adminToken, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('chaleur_admin_token');
    setAdminToken(null);
    navigate('/admin/login');
  };

  if (!adminToken) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-foreground text-primary-foreground transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-white/10">
            <Link to="/admin" className="flex items-center">
              <img 
                src="/images/brand/chaleur-logo-black.png" 
                alt="Chaleur Admin" 
                className="h-8 w-auto object-contain brightness-0 invert"
              />
              <span className="ml-2 text-xs uppercase tracking-wider opacity-60">Admin</span>
            </Link>
            <button 
              className="lg:hidden p-2"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-none text-sm transition-colors ${
                    isActive 
                      ? 'bg-white/10 text-white' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-sm text-white/60 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <button 
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-4 ml-auto">
            <Link to="/" target="_blank" className="text-sm text-muted-foreground hover:text-foreground">
              View Store →
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
